import React, { useState, useEffect, useRef, useContext } from "react";
import { Context as AuthContext } from "../../../context/AuthContext";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import Loader from "../../common/loader";
import LazyImage from "../../common/LazyImage";
import {
  sportImgByName,
  amenityIconByName,
  BOOKING_STEPS,
  CONTENT_NAV,
} from "./facilityDetailConstants";
import TimeSlotPicker from "./TimeSlotPicker";
const FacilityDetailView = ({
  facility,
  images,
  IMG_URL,
  categoryLabel,
  reviews,
  reviewsLoading,
  StarRating,
  nearbyFacilities,
  nearbyLoading,
  nearbyError,
  onRetryLocation,
  navigate,
  activeTab,
  onTabClick,
  isOpen,
  setIsOpen,
  photoIndex,
  setPhotoIndex,
  handleShare,
  bookingStep,
  value,
  onDateChange,
  servicedata,
  selectedService,
  setSelectedService,
  setSelectedSlots,
  setBookedSlots,
  checkServiceDisabled,
  courtId,
  selectedCourtData,
  handleCourtChange,
  setSlotsPrice,
  setDuration,
  currentTimeSlots,
  isPastSlot,
  isSlotBooked,
  handleSlotClick,
  selectedSlots,
  calculateTotalPrice,
  state,
  handleToBookNow,
  onClearBooking,
}) => {
  const { openLoginModal } = useContext(AuthContext);
  const [bannerSlide, setBannerSlide] = useState(0);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const aboutDescription =
    facility.description?.trim() || "No description available.";
  const aboutNeedsToggle = aboutDescription.length > 260;
  const slideImages = images.length > 0 ? images : [];
  const heroImg = slideImages[bannerSlide] || slideImages[0];
  useEffect(() => {
    if (bannerSlide >= slideImages.length) {
      setBannerSlide(0);
    }
  }, [slideImages.length, bannerSlide]);

  useEffect(() => {
    setAboutExpanded(false);
  }, [facility?.id]);

  const navItems = CONTENT_NAV;

  const scrollFromTabRef = useRef(false);
  const sideNavRef = useRef(null);
  const activeTabRef = useRef(activeTab);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const selectTab = (id, e) => {
    e?.preventDefault?.();
    scrollFromTabRef.current = true;
    onTabClick(id);
    const el = document.getElementById(`fv-section-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.setTimeout(() => {
        scrollFromTabRef.current = false;
      }, 700);
    } else {
      scrollFromTabRef.current = false;
    }
  };

  useEffect(() => {
    const sectionEls = navItems
      .map((item) => document.getElementById(`fv-section-${item.id}`))
      .filter(Boolean);

    if (!sectionEls.length) return undefined;

    let rafId = null;
    const updateActiveFromScroll = () => {
      if (scrollFromTabRef.current) return;

      const navEl = sideNavRef.current;
      if (!navEl) return;

      const anchorY = navEl.getBoundingClientRect().bottom + 12;
      let bestId = null;
      let bestTop = -Infinity;

      for (const el of sectionEls) {
        const top = el.getBoundingClientRect().top;
        if (top <= anchorY && top > bestTop) {
          bestTop = top;
          bestId = el.getAttribute("data-section-id");
        }
      }

      if (bestId && bestId !== activeTabRef.current) {
        onTabClick(bestId);
      }
    };

    const onScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateActiveFromScroll);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    updateActiveFromScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [navItems, onTabClick]);

  const canBook =
    value && courtId && selectedService && selectedSlots.length > 0;

  const bookingStepHints = {
    1: "Pick a day for your session",
    2: "Select sport or activity",
    3: "Choose a court or lane",
    4: "Choose a time period, then tap a slot",
  };

  const bookCtaLabel = () => {
    if (canBook) {
      return state.userData
        ? `Confirm · ₹${calculateTotalPrice()}`
        : "Sign in to book";
    }
    if (bookingStep === 1) return "Choose a date";
    if (bookingStep === 2) return "Choose an activity";
    if (bookingStep === 3) return "Choose a court";
    return "Select time slots";
  };

  const scrollToBook = (e) => {
    e?.preventDefault?.();
    document
      .getElementById("fv-book-panel")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const formatChipDate = (d) =>
    d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });

  const activityName =
    servicedata?.find((s) => s.id === selectedService)?.name || null;

  const hasBookingDraft =
    value || selectedService || courtId || selectedSlots.length > 0;

  const renderWizardStep = () => {
    switch (bookingStep) {
      case 1:
        return (
          <div className="fv-wizard__panel fv-wizard__panel--date">
            <Calendar
              onChange={onDateChange}
              value={value}
              minDate={new Date()}
              showNeighboringMonth={false}
              className="fv-calendar"
            />
          </div>
        );
      case 2:
        if (!value) {
          return <p className="fv-wizard__empty">Choose a date first.</p>;
        }
        if (!servicedata?.length) {
          return (
            <p className="fv-wizard__empty">No activities on this date.</p>
          );
        }
        return (
          <div className="fv-wizard__panel fv-wizard__panel--activity">
            <div className="fv-activity-rail" role="group" aria-label="Activities">
              {servicedata.map((item, ind) => {
                const selected = selectedService === item.id;
                return (
                  <button
                    key={item.name + ind}
                    type="button"
                    aria-pressed={selected}
                    className={`fv-activity-chip ${
                      selected ? "is-selected" : ""
                    }`}
                    disabled={checkServiceDisabled(item.id)}
                    onClick={() => {
                      setSelectedService(item.id);
                      setSelectedSlots([]);
                      setBookedSlots([]);
                    }}
                  >
                    <span className="fv-activity-chip__media">
                      <LazyImage src={sportImgByName(item.name)} alt="" />
                    </span>
                    <span className="fv-activity-chip__label text-capitalize">
                      {item.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      case 3:
        if (!selectedService) {
          return <p className="fv-wizard__empty">Pick an activity first.</p>;
        }
        return (
          <div className="fv-wizard__panel fv-wizard__panel--court">
            <div className="fv-court-rail" role="group" aria-label="Courts">
              {servicedata
                .find((item) => item.id === selectedService)
                ?.court?.map((court, index) => {
                  const selected = courtId === court.id;
                  return (
                    <button
                      key={court.court_name + index}
                      type="button"
                      aria-pressed={selected}
                      className={`fv-court-chip ${
                        selected ? "is-selected" : ""
                      }`}
                      onClick={() => {
                        setSlotsPrice(court.day_slot_price);
                        setDuration(court.duration);
                        handleCourtChange(court);
                      }}
                    >
                      <span className="fv-court-chip__name">
                        {court.court_name}
                      </span>
                      <span className="fv-court-chip__price">
                        ₹{court.day_slot_price}
                        <span className="fv-court-chip__unit">/ day</span>
                      </span>
                    </button>
                  );
                }) ?? (
                <p className="fv-wizard__empty">No courts.</p>
              )}
            </div>
          </div>
        );
      case 4:
      default:
        if (!courtId) {
          return <p className="fv-wizard__empty">Select a court first.</p>;
        }
        return (
          <div className="fv-wizard__panel fv-wizard__panel--time">
            <TimeSlotPicker
              currentTimeSlots={currentTimeSlots}
              isPastSlot={isPastSlot}
              isSlotBooked={isSlotBooked}
              selectedSlots={selectedSlots}
              onSlotClick={handleSlotClick}
            />
          </div>
        );
    }
  };

  const renderSectionContent = (sectionId) => {
    switch (sectionId) {
      case "amenities":
        return facility.amenities?.length > 0 ? (
          <ul className="fv-amenity-grid">
            {facility.amenities.map((amenity, index) => {
              const amenityName =
                amenity.name || amenity.amenity_name || amenity.title || "Amenity";
              return (
                <li key={amenityName + index}>
                  <div className="fv-amenity-card">
                    <span className="fv-amenity-card__icon" aria-hidden="true">
                      <i className={amenityIconByName(amenityName)} />
                    </span>
                    <span className="fv-amenity-card__name">{amenityName}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="fv-empty">No amenities listed.</p>
        );
      case "gallery":
        return images.length > 0 ? (
          <div className="fv-gallery">
            <div className="fv-gallery__grid">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`fv-gallery__cell${
                    idx === 0 && images.length > 2
                      ? " fv-gallery__cell--feature"
                      : ""
                  }`}
                  onClick={() => {
                    setPhotoIndex(idx);
                    setIsOpen(true);
                  }}
                >
                  <LazyImage src={`${IMG_URL}${img}`} alt="" />
                  <span className="fv-gallery__overlay" aria-hidden="true">
                    <i className="fa-solid fa-expand" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <p className="fv-empty">No photos yet.</p>
        );
      case "reviews": {
        if (reviewsLoading) {
          return <p className="fv-empty">Loading reviews…</p>;
        }
        if (!reviews.length) {
          return (
            <div className="fv-empty-state">
              <span className="fv-empty-state__icon" aria-hidden="true">
                <i className="fa-regular fa-star" />
              </span>
              <p>Be the first to review after your booking.</p>
            </div>
          );
        }
        const avgRating =
          reviews.reduce((sum, r) => sum + (Number(r.rating) || 0), 0) /
          reviews.length;
        return (
          <>
            <div className="fv-reviews-summary">
              <div className="fv-reviews-summary__score">
                <strong>{avgRating.toFixed(1)}</strong>
                <StarRating rating={Math.round(avgRating)} />
              </div>
              <span className="fv-reviews-summary__count">
                {reviews.length} review{reviews.length !== 1 ? "s" : ""}
              </span>
            </div>
            <ul className="fv-reviews">
              {reviews.map((review, index) => {
                const name = review.user_name || "Guest";
                const initial = name.charAt(0).toUpperCase();
                return (
                  <li key={review.id || index} className="fv-review-card">
                    <div className="fv-review-card__head">
                      <span className="fv-review-card__avatar" aria-hidden="true">
                        {initial}
                      </span>
                      <div className="fv-review-card__meta">
                        <strong>{name}</strong>
                        <time dateTime={review.created_at}>
                          {new Date(review.created_at).toLocaleDateString(
                            "en-IN",
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            },
                          )}
                        </time>
                      </div>
                      <span className="fv-review-card__stars">
                        <StarRating rating={review.rating} />
                      </span>
                    </div>
                    <p className="fv-review-card__body">{review.message}</p>
                  </li>
                );
              })}
            </ul>
          </>
        );
      }
      case "location":
        return (
          <div className="fv-map-card">
            <div className="fv-map-card__frame">
              <iframe
                title="Venue location map"
                src={
                  facility.lat && facility.lng
                    ? `https://www.google.com/maps?q=${facility.lat},${facility.lng}&z=15&output=embed`
                    : `https://www.google.com/maps?q=${encodeURIComponent(
                        facility.address || "Dehradun, India",
                      )}&z=15&output=embed`
                }
                loading="lazy"
              />
            </div>
            <div className="fv-map-card__footer">
              <i className="fa-solid fa-location-dot" aria-hidden="true" />
              <p>{facility.address || "Address not available"}</p>
            </div>
          </div>
        );
      case "nearby":
        if (nearbyLoading) return <Loader />;
        if (nearbyError) {
          return (
            <div className="fv-empty-state fv-empty-state--error">
              <p>{nearbyError}</p>
              {nearbyError.includes("location") && (
                <button
                  type="button"
                  className="fv-btn fv-btn--primary"
                  onClick={onRetryLocation}
                >
                  Enable location
                </button>
              )}
            </div>
          );
        }
        if (!nearbyFacilities.length) {
          return <p className="fv-empty">No nearby venues found.</p>;
        }
        return (
          <div className="fv-nearby-rail" role="list">
            {nearbyFacilities.map((f, index) => {
              const imgSrc = f.featured_image?.replace(/\\/g, "/") || "";
              const priceLabel =
                f.minimum_amount && f.minimum_amount !== "N/A"
                  ? `₹${f.minimum_amount}`
                  : null;
              return (
                <article
                  key={f.id || f.slug || index}
                  className="fv-nearby-rail__item"
                  role="listitem"
                >
                  <button
                    type="button"
                    className="fv-nearby-chip"
                    onClick={() => navigate(`/facility/${f.slug}`)}
                  >
                    <span className="fv-nearby-chip__img">
                      {imgSrc ? (
                        <LazyImage src={`${IMG_URL}${imgSrc}`} alt="" />
                      ) : (
                        <span
                          className="fv-nearby-chip__placeholder"
                          aria-hidden="true"
                        >
                          <i className="fa-solid fa-building" />
                        </span>
                      )}
                      <span className="fv-nearby-chip__dist">
                        {f.distance?.toFixed(1)} km
                      </span>
                    </span>
                    <span className="fv-nearby-chip__body">
                      <strong className="fv-nearby-chip__name">
                        {f.official_name}
                      </strong>
                      <span className="fv-nearby-chip__foot">
                        {priceLabel ? (
                          <span className="fv-nearby-chip__price">
                            From {priceLabel}
                          </span>
                        ) : (
                          <span className="fv-nearby-chip__link">View venue</span>
                        )}
                      </span>
                    </span>
                  </button>
                </article>
              );
            })}
          </div>
        );
      case "about":
      default:
        return (
          <div
            className={`fv-about ${aboutExpanded ? "is-expanded" : ""} ${
              aboutNeedsToggle ? "has-toggle" : ""
            }`}
          >
            <div className="fv-about__clip">
              <div className="fv-prose">
                <p>{aboutDescription}</p>
              </div>
            </div>
            {aboutNeedsToggle ? (
              <button
                type="button"
                className="fv-about__toggle"
                aria-expanded={aboutExpanded}
                onClick={() => setAboutExpanded((open) => !open)}
              >
                {aboutExpanded ? "View less" : "Read more"}
                <i
                  className={`fa-solid ${
                    aboutExpanded ? "fa-chevron-up" : "fa-chevron-down"
                  }`}
                  aria-hidden="true"
                />
              </button>
            ) : null}
          </div>
        );
    }
  };

  return (
    <div className="fv-page">
      <div className="fv-split">
        <div className="fv-split__main">
      {/* Venue banner */}
      <header className="fv-hero">
        <div className="fv-hero__inner">
          <div className="fv-hero__panel">
          <div className="fv-hero__row">
            <div className="fv-hero__visual">
              <div className="fv-hero__slider">
                {heroImg ? (
                  <button
                    type="button"
                    className="fv-hero__slide"
                    onClick={() => {
                      setPhotoIndex(bannerSlide);
                      setIsOpen(true);
                    }}
                    aria-label="Open venue photos"
                  >
                    <LazyImage
                      key={heroImg}
                      src={`${IMG_URL}${heroImg}`}
                      alt={facility.official_name || "Venue"}
                      loading="eager"
                    />
                  </button>
                ) : (
                  <div className="fv-hero__slide fv-hero__slide--empty">
                    <LazyImage src={sportImgByName(categoryLabel)} alt="" />
                  </div>
                )}

                {slideImages.length > 1 && (
                  <>
                    <button
                      type="button"
                      className="fv-hero__nav fv-hero__nav--prev"
                      aria-label="Previous photo"
                      onClick={() =>
                        setBannerSlide(
                          (bannerSlide - 1 + slideImages.length) %
                            slideImages.length,
                        )
                      }
                    >
                      <i className="fa-solid fa-chevron-left" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="fv-hero__nav fv-hero__nav--next"
                      aria-label="Next photo"
                      onClick={() =>
                        setBannerSlide((bannerSlide + 1) % slideImages.length)
                      }
                    >
                      <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                    </button>
                    <span className="fv-hero__count" aria-live="polite">
                      {bannerSlide + 1} / {slideImages.length}
                    </span>
                  </>
                )}

                {slideImages.length > 0 && (
                  <button
                    type="button"
                    className="fv-hero__gallery"
                    onClick={() => {
                      setPhotoIndex(bannerSlide);
                      setIsOpen(true);
                    }}
                  >
                    <i className="fa-solid fa-images" aria-hidden="true" />
                    Gallery
                  </button>
                )}
              </div>

              {slideImages.length > 1 && (
                <div className="fv-hero__thumbs" role="tablist" aria-label="Photos">
                  {slideImages.slice(0, 5).map((img, idx) => (
                    <button
                      key={img + idx}
                      type="button"
                      role="tab"
                      aria-selected={bannerSlide === idx}
                      className={bannerSlide === idx ? "is-active" : ""}
                      aria-label={`Photo ${idx + 1}`}
                      onClick={() => setBannerSlide(idx)}
                    >
                      <LazyImage src={`${IMG_URL}${img}`} alt="" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="fv-hero__content">
              <div className="fv-hero__top">
                <span className="fv-hero__eyebrow">
                  <LazyImage src={sportImgByName(categoryLabel)} alt="" />
                  {categoryLabel}
                </span>
                <button
                  type="button"
                  className="fv-btn fv-btn--ghost fv-hero__btn fv-hero__btn--share"
                  onClick={() => handleShare("generic")}
                >
                  <i className="fa-solid fa-share-nodes" aria-hidden="true" />
                  Share
                </button>
              </div>

              <h1 className="fv-hero__title">
                {facility.official_name || "Venue"}
              </h1>

              <p className="fv-hero__loc">
                <i className="fa-solid fa-location-dot" aria-hidden="true" />
                {facility.address || "Address not available"}
              </p>

              <div className="fv-hero__pills">
                <span className="fv-hero__pill">
                  <i className="fa-solid fa-circle-check" aria-hidden="true" />
                  Verified venue
                </span>
                <span className="fv-hero__pill">
                  <i className="fa-solid fa-bolt" aria-hidden="true" />
                  Instant booking
                </span>
              </div>

              <div className="fv-hero__stats">
                <div className="fv-hero__stat">
                  <i className="fa-solid fa-indian-rupee-sign" aria-hidden="true" />
                  <div>
                    <span>Starting at</span>
                    <strong>₹{facility.minimum_amount || 0}</strong>
                  </div>
                </div>
                <div className="fv-hero__stat">
                  <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                  <div>
                    <span>Availability</span>
                    <strong>Book today</strong>
                  </div>
                </div>
              </div>

              <div className="fv-hero__footer">
                <button
                  type="button"
                  className="fv-btn fv-btn--primary fv-hero__btn fv-hero__btn--book"
                  onClick={scrollToBook}
                >
                  <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                  Book a slot
                </button>
              </div>
            </div>
          </div>
          </div>
        </div>
      </header>

      {/* Details: section nav + content */}
      <div className="fv-stage">
        <div className="fv-stage__layout">
          <nav
            className="fv-side-nav"
            ref={sideNavRef}
            role="tablist"
            aria-label="Venue sections"
          >
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={activeTab === item.id}
                className={`fv-side-nav__btn ${
                  activeTab === item.id ? "is-active" : ""
                }`}
                onClick={(e) => selectTab(item.id, e)}
                title={item.label}
              >
                <i className={item.icon} aria-hidden="true" />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>

          <main className="fv-content" aria-label="Venue details">
            {navItems.map((item) => (
              <section
                key={item.id}
                id={`fv-section-${item.id}`}
                data-section-id={item.id}
                className={`fv-section fv-section--${item.id}`}
                aria-labelledby={`fv-section-heading-${item.id}`}
              >
                <header className="fv-section__head">
                  <span className="fv-section__icon" aria-hidden="true">
                    <i className={item.icon} />
                  </span>
                  <div className="fv-section__titles">
                    <h2 id={`fv-section-heading-${item.id}`}>{item.label}</h2>
                    <p className="fv-section__desc">{item.hint}</p>
                  </div>
                  {item.id === "gallery" && images.length > 0 ? (
                    <span className="fv-section__badge">
                      {images.length} photos
                    </span>
                  ) : null}
                  {item.id === "amenities" &&
                  facility.amenities?.length > 0 ? (
                    <span className="fv-section__badge">
                      {facility.amenities.length}
                    </span>
                  ) : null}
                  {item.id === "nearby" && nearbyFacilities.length > 0 ? (
                    <span className="fv-section__badge">
                      {nearbyFacilities.length} venues
                    </span>
                  ) : null}
                </header>
                <div className="fv-section__body">
                  {renderSectionContent(item.id)}
                </div>
              </section>
            ))}
          </main>
        </div>
      </div>
        </div>

        <aside className="fv-split__book" aria-label="Book a slot">
          <div className="fv-dock" id="book">
            <div className="fv-dock__panel fv-dock__panel--compact" id="fv-book-panel">
              <header
                className="fv-dock__head"
                style={{
                  "--fv-booking-progress": `${((bookingStep - 1) / 3) * 100}%`,
                }}
              >
                <h2 className="fv-dock__venue">
                  {facility.official_name || "Venue"}
                </h2>
                <ol
                  className="fv-dock__stepper"
                  aria-label="Booking progress"
                >
                  {BOOKING_STEPS.map(({ step, label, icon }) => (
                    <li
                      key={step}
                      className={`fv-dock__stepper-item ${
                        bookingStep === step
                          ? "is-current"
                          : bookingStep > step
                            ? "is-done"
                            : ""
                      }`}
                      title={label}
                    >
                      <span
                        className="fv-dock__stepper-icon"
                        aria-hidden="true"
                      >
                        {bookingStep > step ? (
                          <i className="fa-solid fa-check" />
                        ) : (
                          <i className={icon} />
                        )}
                      </span>
                      <span className="fv-dock__stepper-label">{label}</span>
                    </li>
                  ))}
                </ol>
              </header>

              <div className="fv-dock__scroll">
                <p className="fv-dock__step-caption">
                  {bookingStepHints[bookingStep]}
                </p>
                <div className="fv-dock__form" key={bookingStep}>
                  {renderWizardStep()}
                </div>
              </div>

              <footer className="fv-dock__bar">
                <div
                  className="fv-dock__summary"
                  aria-label="Your booking"
                >
                  <div
                    className={`fv-dock__pill ${value ? "is-set" : ""}`}
                  >
                    <i
                      className="fa-solid fa-calendar-day"
                      aria-hidden="true"
                    />
                    <span className="fv-dock__pill-text">
                      <span className="fv-dock__pill-name">Date</span>
                      <span className="fv-dock__pill-value">
                        {value ? formatChipDate(value) : "—"}
                      </span>
                    </span>
                  </div>
                  <div
                    className={`fv-dock__pill ${
                      selectedService ? "is-set" : ""
                    }`}
                  >
                    <i className="fa-solid fa-bolt" aria-hidden="true" />
                    <span className="fv-dock__pill-text">
                      <span className="fv-dock__pill-name">Activity</span>
                      <span
                        className="fv-dock__pill-value"
                        title={activityName || undefined}
                      >
                        {activityName || "—"}
                      </span>
                    </span>
                  </div>
                  <div
                    className={`fv-dock__pill ${courtId ? "is-set" : ""}`}
                  >
                    <i
                      className="fa-solid fa-table-cells-large"
                      aria-hidden="true"
                    />
                    <span className="fv-dock__pill-text">
                      <span className="fv-dock__pill-name">Court</span>
                      <span
                        className="fv-dock__pill-value"
                        title={selectedCourtData?.court_name || undefined}
                      >
                        {selectedCourtData?.court_name || "—"}
                      </span>
                    </span>
                  </div>
                  <div
                    className={`fv-dock__pill ${
                      selectedSlots.length ? "is-set" : ""
                    }`}
                  >
                    <i className="fa-solid fa-clock" aria-hidden="true" />
                    <span className="fv-dock__pill-text">
                      <span className="fv-dock__pill-name">Time</span>
                      <span className="fv-dock__pill-value">
                        {selectedSlots.length
                          ? `${selectedSlots.length} slot${
                              selectedSlots.length > 1 ? "s" : ""
                            }`
                          : "—"}
                      </span>
                    </span>
                  </div>
                </div>

                <div className="fv-dock__pay">
                  <div className="fv-dock__pay-meta">
                    <span className="fv-dock__pay-label">Total</span>
                    <strong className="fv-dock__pay-amount">
                      {selectedSlots.length > 0
                        ? `₹${calculateTotalPrice()}`
                        : "—"}
                    </strong>
                  </div>
                  <div className="fv-dock__pay-actions">
                    {hasBookingDraft ? (
                      <button
                        type="button"
                        className="fv-dock__pay-clear"
                        onClick={onClearBooking}
                        aria-label="Clear booking"
                      >
                        <i
                          className="fa-solid fa-rotate-left"
                          aria-hidden="true"
                        />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="fv-dock__pay-btn"
                      disabled={!canBook}
                      onClick={
                        state.userData
                          ? handleToBookNow
                          : () => openLoginModal(!state.loginState)
                      }
                    >
                      {bookCtaLabel()}
                    </button>
                  </div>
                </div>

                <p className="fv-dock__note">
                  <i className="fa-solid fa-lock" aria-hidden="true" />
                  Secure payment · Instant confirmation
                </p>
              </footer>
            </div>
          </div>
        </aside>
      </div>

      {isOpen && images.length > 0 && (
        <Lightbox
          mainSrc={`${IMG_URL}${images[photoIndex]}`}
          nextSrc={`${IMG_URL}${images[(photoIndex + 1) % images.length]}`}
          prevSrc={`${IMG_URL}${
            images[(photoIndex + images.length - 1) % images.length]
          }`}
          onCloseRequest={() => setIsOpen(false)}
          onMovePrevRequest={() =>
            setPhotoIndex((photoIndex + images.length - 1) % images.length)
          }
          onMoveNextRequest={() =>
            setPhotoIndex((photoIndex + 1) % images.length)
          }
        />
      )}

      <div className="fv-mobile-bar">
        <div className="fv-mobile-bar__price">
          <span>From</span>
          <strong>₹{facility.minimum_amount || 0}</strong>
        </div>
        <button type="button" onClick={scrollToBook}>
          {canBook
            ? `Confirm · ₹${calculateTotalPrice()}`
            : "Book a slot"}
        </button>
      </div>

    </div>
  );
};

export default FacilityDetailView;
